import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  isAuthenticated,
  login,
  diligentLogin,
  getClientList,
  handleLoginToken,
  isAdmin,
} from "../../services/accountService";
import { defaultTitle } from "../../config";
import handleApiError from "../../helpers/handleApiError";
import { Helmet } from "react-helmet";
import { pages, pdfLinks } from "../../constants";
import { BsX } from "react-icons/bs";
import { useQueryClient } from "@tanstack/react-query";

import RedirectOnLogging from "./RedirectOnLogging";
import { toast } from "react-toastify";

// const tabs = {
//   clientLogin: "clientLogin",
//   employeeLogin: "employeeLogin",
// };

export const NewLoginFormV2OneFormForAllLoginTypes = ({
  setLoading,
  setLastLoginUpdate,
}) => {
  const history = useHistory();
  const queryClient = useQueryClient();

  const [loginInfo, setLoginInfo] = useState({ username: "", password: "" });
  const [clientsRetrieved, setClientsRetrieved] = useState([]);
  const [clientSelectionValue, setClientSelectionValue] = useState("");
  const [clientSelectionFocused, setClientSelectionFocused] = useState(false);
  const [isDiligentMasterLogin, setIsDiligentMasterLogin] = useState(false);
  const filteredClientsRetrieved = clientsRetrieved.filter((client) => {
    return client.clientSearchTerm
      .toLowerCase()
      .includes(clientSelectionValue.toLowerCase());
  });

  const maxComboboxOptions = 300;
  const clientListOptions = useMemo(() => {
    const sortedAdminLoginToTopOfClientList = filteredClientsRetrieved.sort(
      (a, b) => {
        if (a.clientId === 99999) {
          return -1;
        } else if (b.clientId === 99999) {
          return 1;
        } else {
          return a.masterName.localeCompare(b.masterName);
        }
      }
    );
    if (filteredClientsRetrieved.length > 0) {
      const filteredClientsToDisplay = sortedAdminLoginToTopOfClientList.slice(
        0,
        maxComboboxOptions
      );
      return (
        <>
          {filteredClientsToDisplay.map((client, index) => {
            return (
              <div
                className="combobox-option"
                id="combobox-option"
                key={client.clientId + "-" + index}
                onClick={() => {
                  setClientSelectionValue(client.clientSearchTerm);
                  setClientSelectionFocused(false);
                }}
              >
                {client.clientSearchTerm}
              </div>
            );
          })}
          {filteredClientsToDisplay.length < clientsRetrieved.length && (
            <div className="combobox-option combobox-option-disabled">
              {filteredClientsToDisplay.length}/{clientsRetrieved.length}{" "}
              Clients shown
              {clientSelectionValue
                ? ""
                : " - begin searching to show more options"}
            </div>
          )}
        </>
      );
    } else {
      return (
        <div className="combobox-option combobox-option-disabled">
          No results found
        </div>
      );
    }
    // return filteredClientsRetrieved.length > 0 ? (
    //   <>
    //     {filteredClientsRetrieved
    //       .slice(0, maxComboboxOptions)
    //       .map((client, index) => {
    //         return (
    //           <div className="combobox-option">
    //             {client.clientId} - {client.masterName}
    //           </div>
    //         );
    //       })}
    //     {clientsRetrieved.length > maxComboboxOptions && (
    //       <div className="combobox-option">
    //         Begin searching for more options.
    //       </div>
    //     )}
    //   </>
    // ) : (
    //   <div className="combobox-option combobox-option-disabled">
    //     No results found
    //   </div>
    // );
  }, [filteredClientsRetrieved.length]);

  const handleChangeClientSelectionValue = (e) => {
    setClientSelectionValue(e.target.value);
  };
  const handleClickIntoCombobox = (e) => {
    setClientSelectionFocused(true);
  };
  // const handleClickOutOfCombobox = (e) => {
  //   // setClientSelectionFocused(false);
  // };

  const comboboxContainerRef = useRef();
  const handleClickOutsideOfCombobox = useCallback(
    function handleClickOutsideOfCombobox(e) {
      if (
        comboboxContainerRef.current &&
        !comboboxContainerRef.current.contains(e.target)
      ) {
        setClientSelectionFocused(false);
      }
    },
    []
  );

  const escapeCombobox = useCallback(function escapeCombobox(e) {
    // console.log(e.key);
    if (e.key === "Escape") {
      setClientSelectionFocused(false);
    }
  }, []);

  useEffect(() => {
    //"mousedown" feels better than "mouseup" or "click" (which works similar to "mouseup" but I believe only if mousedown occured within the same element as well. (in this case the element would be the entire document))
    document.addEventListener("mousedown", handleClickOutsideOfCombobox);
    document.addEventListener("keydown", escapeCombobox);

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideOfCombobox);
      document.removeEventListener("keydown", escapeCombobox);
    };
  }, []);
  // console.log(clientSelectionFocused);

  const handleClearComboboxSelection = (e) => {
    setClientSelectionValue("");
    setClientSelectionFocused(false);
  };

  const handleChangeLoginInfoFormValues = (e) => {
    setLoginInfo({ ...loginInfo, [e.target.name]: e.target.value });
  };

  //   const handleClickRetrieveClients = async (e) => {
  //     e.preventDefault();
  //     try {
  //       setLoading(true);
  //       await sleep(1500);
  //       if (loginInfo.username === "chad" && loginInfo.password === "giga") {
  //         setClientsRetrieved(dummyClients);
  //       } else {
  //         toast.error("LOL");
  //       }

  //       setLoading(false);
  //     } catch (ex) {
  //       handleApiError(ex);
  //       setLoading(false);
  //     }
  //   };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      //if we set isDiligentMasterLogin to true on the first submission, we know we are trying to login to the Diligent Master Login route
      if (isDiligentMasterLogin) {
        const selectedClient = clientsRetrieved.find((client) => {
          return client.clientSearchTerm === clientSelectionValue;
        });
        if (!selectedClient) {
          toast.error("Please select a client to log in as.");
          setLoading(false);
          return;
        }
        const response = await diligentLogin({
          username: loginInfo.username,
          password: loginInfo.password,
          clientId: selectedClient.clientId,
          branchId: selectedClient.branchId,
        });
        setLoading(false);
        let token = response.data;

        handleLoginToken(
          token,
          false,
          loginInfo.username + " - " + selectedClient.clientId
        );
        if (isAdmin()) {
          history.push(
            await import("../Admin/adminConstants").then((module) => {
              return module.adminRoutes.adminHome.url;
            })
          );
        } else {
          history.push(pages.mainPage.url);
        }
      } else {
        //Else use the main login route
        const response = await login(loginInfo.username, loginInfo.password);
        let token = response.data;
        //If the response is MasterUser, set isDiligentMasterLogin to true, and retrieve our client list for our employee to select from
        if (response.data === "MasterUser") {
          setIsDiligentMasterLogin(true);
          const response = await getClientList(
            loginInfo.username,
            loginInfo.password
          );
          const clientsRetrievedList = response.data;
          const processedClientsRetrievedList = clientsRetrievedList.map(
            (client) => {
              return {
                clientSearchTerm: client.clientId + " - " + client.masterName,
                ...client,
              };
            }
          );
          setClientsRetrieved(processedClientsRetrievedList);
        } else {
          //If the response was not MasterUser, check to see if it is an array.
          //For our normal client login we receive an array of length two, [0] is the access JWT, [1] is the refresh JWT, which we are currently not using (4/2023) because of issues it was giving us with multiple people/devices using the same account
          //If it is not an array, we received a token for the customer login.
          //But actually login() from accountservice, calls two different endpoints depending if the username was an email
          //we could instead split it to two functions, login/customerLogin, that call their own respective endpoints
          //and check which to call here, based on email again, and perform separate logic based on that instead of whether we receive an array or not.

          handleLoginToken(token);
          setLastLoginUpdate(new Date());
          setLoading(false);
        }
        setLoading(false);
      }

      queryClient.resetQueries();
    } catch (ex) {
      setLoading(false);
      handleApiError(ex);
    }
  };

  return isAuthenticated(undefined, undefined, pages.loginPage.url) ? ( //if we area already logged in and try to come to the login page, redirect to order-history
    <RedirectOnLogging />
  ) : (
    <>
      <Helmet>
        <title>Login - {defaultTitle}</title>
      </Helmet>
      <div className="p-2 d-flex justify-content-center mt-2">
        <div style={{ width: "550px" }}>
          <div className="paper-main-login pl-4 pr-4 pl-sm-5 pr-sm-5 pb-4">
            {/* header */}
            <div style={{ textAlign: "center" }}>
              <h2>Login</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Username</label>
                <input
                  className="form-control"
                  name="username"
                  onChange={handleChangeLoginInfoFormValues}
                  required
                  value={loginInfo.username}
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  className="form-control"
                  type="password"
                  name="password"
                  onChange={handleChangeLoginInfoFormValues}
                  required
                  value={loginInfo.password}
                />
              </div>
              {/* Combobox */}
              {clientsRetrieved?.length > 0 && (
                <div
                  style={{
                    position: "relative",
                    // backgroundColor: "red"
                  }}
                  className="form-group"
                  ref={comboboxContainerRef}
                >
                  <label>Client List</label>
                  <div
                    style={{
                      width: "100%",
                      borderRadius: ".25rem",
                      padding: "0.375rem 0.75rem",
                      border: "1px solid #ced4da",
                      boxShadow: "none",
                      outline: "none",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "10px",
                      paddingRight: ".25rem",
                    }}
                  >
                    <input
                      style={{
                        outline: "none",
                        boxShadow: "none",
                        border: "none",
                        flex: 1,
                      }}
                      id="combobox-input"
                      value={clientSelectionValue}
                      onChange={handleChangeClientSelectionValue}
                      onClick={handleClickIntoCombobox}
                    />
                    {clientSelectionValue && (
                      <div>
                        <button
                          type="button"
                          style={{
                            border: "none",
                            outline: "none",
                            backgroundColor: "rgba(0,0,0,0)",
                          }}
                          onClick={handleClearComboboxSelection}
                        >
                          <BsX size={20} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      border: "1px solid gray",
                      backgroundColor: "white",
                      zIndex: 2,
                      width: "100%",
                      borderRadius: "5px",
                      // textAlign: "left",
                      display: clientSelectionFocused ? "" : "none",
                      maxHeight: "300px",
                      overflow: "auto",
                    }}
                  >
                    {/* {filteredClientsRetrieved.length > 0 ? (
                          filteredClientsRetrieved.map((client, index) => {
                            return (
                              <div className="combobox-option">
                                {client.clientId} - {client.masterName}
                              </div>
                            );
                          })
                        ) : (
                          <div className="combobox-option combobox-option-disabled">
                            No results found
                          </div>
                        )} */}
                    {clientListOptions}
                  </div>
                </div>
              )}
              <p>
                By logging in, you agree to our{" "}
                <Link
                  target="_blank"
                  rel="noopener noreferrer" //might not be necessary unless linking to a different site or in modern browsers. not 100% sure, need to research
                  to={pdfLinks.termsAndConditionsLogin}
                >
                  terms and conditions
                </Link>
                .
              </p>
              <center>
                <button className="btn btn-primary">Login</button>
              </center>
            </form>
          </div>
          <div className="text-center mt-3 font-semi-small text-note">
            Diligent clients as well as their customers may use this screen to
            log in.
          </div>
        </div>
      </div>
    </>
  );
};
